import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import styles from "./Button.module.css";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "danger"
  | "ghost"
  | "accent"
  | "dangerOutline";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  loadingText?: string;
  fullWidth?: boolean;
  iconOnly?: boolean;
  children: ReactNode;
}

export function getButtonClassName(
  variant: ButtonVariant,
  size: ButtonSize,
  options?: {
    fullWidth?: boolean;
    iconOnly?: boolean;
    disabled?: boolean;
    className?: string;
  },
): string {
  const classes = [styles.btn, styles[variant]];

  if (size !== "md") {
    classes.push(styles[size]);
  }

  if (options?.fullWidth) {
    classes.push(styles.fullWidth);
  }

  if (options?.iconOnly) {
    classes.push(styles.iconOnly);
  }

  if (options?.disabled) {
    classes.push(styles.btnDisabled);
  }

  if (options?.className) {
    classes.push(options.className);
  }

  return classes.join(" ");
}

export const buttonStyles = styles;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      loading = false,
      loadingText,
      fullWidth = false,
      iconOnly = false,
      disabled,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const buttonClassName = getButtonClassName(variant, size, {
      fullWidth,
      iconOnly,
      disabled: disabled || loading,
      className,
    });

    return (
      <button ref={ref} className={buttonClassName} disabled={disabled || loading} {...props}>
        {loading ? (
          <>
            <span className={styles.loadingSpinner} aria-hidden="true" />
            {loadingText || children}
          </>
        ) : (
          children
        )}
      </button>
    );
  },
);

Button.displayName = "Button";
